import type { TRPCLink } from '@trpc/client';
import type { AnyTRPCRouter } from '@trpc/server';
import { observable } from '@trpc/server/observable';
import type { TokenRefresher } from './token-refresher';

export interface AuthRefreshLinkOptions {
  readonly refresh: TokenRefresher;
  /** 재발급까지 실패한 최종 만료 — 스토어를 비워 보호 라우트가 로그인으로 보낸다. */
  readonly onSessionExpired: () => void;
}

/**
 * 재발급을 시도하지 않는 프로시저 — UNAUTHORIZED 가 "세션 만료"를 뜻하지 않는 경로.
 *
 * 로그인 · 가입 실패(UNAUTHORIZED)는 세션 만료가 아니다. 여기서 재발급을 타면
 * 이미 로그인된 브라우저에서 다른 계정 로그인에 실패했을 때, 무관한 기존 세션의
 * 리프레시 토큰이 조용히 회전되는 부작용이 생긴다.
 *
 * verifyEmail · resetPassword 의 UNAUTHORIZED 는 "메일 링크의 토큰이 무효"라는
 * 뜻이다 — 재발급 후 재시도해도 같은 링크는 계속 무효이므로 즉시 실패시킨다.
 */
const REFRESH_EXEMPT_PATHS: ReadonlySet<string> = new Set([
  'auth.signIn',
  'auth.signUp',
  'auth.refresh',
  'auth.verifyEmail',
  'auth.resetPassword',
]);

/** tRPC 오류에서 코드를 꺼낸다. (링크 단계라 TRPCClientError 인스턴스 검사 대신 형태로 본다) */
const isUnauthorized = (error: unknown): boolean =>
  (error as { data?: { code?: string } } | undefined)?.data?.code === 'UNAUTHORIZED';

/**
 * 액세스 토큰 자동 재발급 링크.
 *
 * UNAUTHORIZED 오류를 만나면 재발급(단일 비행)을 시도하고, 성공 시 원 요청을
 * 정확히 1회 재실행한다. 재발급이 안 되면(리프레시 토큰 없음 · 만료 · 회수)
 * 세션 만료 처리 후 원래 오류를 그대로 전파한다.
 *
 * 소비자가 구독을 해제(언마운트 · 쿼리 취소)한 뒤에는 재시도를 시작하지 않는다 —
 * tRPC observable 의 unsubscribe 는 isDone 을 세우지 않아, 가드 없이는 죽은
 * 소비자에게 새 요청의 결과가 흘러 들어간다.
 */
export const createAuthRefreshLink = (options: AuthRefreshLinkOptions): TRPCLink<AnyTRPCRouter> => {
  return () =>
    ({ op, next }) =>
      observable((observer) => {
        let retried = false;
        let isTornDown = false;
        let subscription = execute();

        function execute(): { unsubscribe: () => void } {
          return next(op).subscribe({
            next: (value) => observer.next(value),
            complete: () => observer.complete(),
            error: (error) => {
              if (!isUnauthorized(error) || retried || REFRESH_EXEMPT_PATHS.has(op.path)) {
                observer.error(error);
                return;
              }

              retried = true;

              options
                .refresh()
                .then((refreshed) => {
                  // 구독 해제 이후에는 재시도도 값 방출도 하지 않는다. (고아 요청 방지)
                  if (isTornDown) {
                    return;
                  }

                  if (!refreshed) {
                    options.onSessionExpired();
                    observer.error(error);
                    return;
                  }

                  // 새 토큰으로 원 요청 재실행 — Authorization 헤더는 요청 시점에
                  // 스토어에서 다시 읽으므로(links.ts) 자동으로 갱신값이 실린다.
                  subscription = execute();
                })
                .catch(() => {
                  if (isTornDown) {
                    return;
                  }

                  options.onSessionExpired();
                  observer.error(error);
                });
            },
          });
        }

        return () => {
          isTornDown = true;
          subscription.unsubscribe();
        };
      });
};
